import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { OpBadge, PayBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { naira, fmtDate, fmtDateTime } from "@/lib/format";

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/customers/${id}`);
      setC(data);
      setForm({
        payment_category: data.payment_category, deposit_percent: data.deposit_percent,
        credit_limit: data.credit_limit, credit_due_days: data.credit_due_days,
        credit_blocked: data.credit_blocked, risk_level: data.risk_level || "normal",
        internal_notes: data.internal_notes || "",
      });
    } catch (e) { toast.error(apiErr(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!c || !form) return <AdminLayout><div className="text-slate-500 py-20 text-center">Loading…</div></AdminLayout>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/customers/${id}`, {
        ...form, deposit_percent: Number(form.deposit_percent), credit_limit: Number(form.credit_limit),
        credit_due_days: Number(form.credit_due_days),
      });
      toast.success("Customer updated");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <AdminLayout>
      <Link to="/admin/customers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0A4F35] mb-5">
        <ArrowLeft size={16} /> Back to customers
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{c.company_name}</h1>
          <p className="mt-1 text-slate-500">{c.phone} · {c.email}</p>
        </div>
        <div className="rounded-lg border border-[#E5E7EB] bg-white px-5 py-3 text-right">
          <p className="text-xs uppercase tracking-widest text-slate-400">Outstanding</p>
          <p className="font-display text-2xl font-extrabold text-[#DC2626]">{naira(c.outstanding)}</p>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* Credit / payment profile */}
        <div className="rounded-lg border border-[#E5E7EB] bg-white">
          <div className="px-5 py-3 border-b border-[#E5E7EB]"><h3 className="font-display font-semibold text-slate-900">Payment & Credit Profile</h3></div>
          <div className="p-5 space-y-4">
            {!isAdmin && <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">Only administrators can change credit terms.</p>}
            <div>
              <Label>Payment category</Label>
              <Select value={form.payment_category} onValueChange={(v) => setForm((s) => ({ ...s, payment_category: v }))} disabled={!isAdmin}>
                <SelectTrigger data-testid="cust-category" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_standard">New / Standard (100% prepaid)</SelectItem>
                  <SelectItem value="deposit">Approved Deposit Customer</SelectItem>
                  <SelectItem value="credit">Approved Credit / Contract</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.payment_category === "deposit" && (
              <div><Label>Default deposit %</Label><Input data-testid="cust-deposit" type="number" value={form.deposit_percent} onChange={(e) => setForm((s) => ({ ...s, deposit_percent: e.target.value }))} disabled={!isAdmin} className="mt-1" /></div>
            )}
            {form.payment_category === "credit" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Credit limit (₦)</Label><Input data-testid="cust-limit" type="number" value={form.credit_limit} onChange={(e) => setForm((s) => ({ ...s, credit_limit: e.target.value }))} disabled={!isAdmin} className="mt-1" /></div>
                <div><Label>Payment due (days)</Label><Input data-testid="cust-days" type="number" value={form.credit_due_days} onChange={(e) => setForm((s) => ({ ...s, credit_due_days: e.target.value }))} disabled={!isAdmin} className="mt-1" /></div>
              </div>
            )}
            <div>
              <Label>Payment-risk level</Label>
              <Select value={form.risk_level} onValueChange={(v) => setForm((s) => ({ ...s, risk_level: v }))} disabled={!isAdmin}>
                <SelectTrigger data-testid="cust-risk" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low risk (reliable)</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High risk (pays late)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-[#E5E7EB] px-3 py-2.5">
              <div><p className="text-sm font-medium text-slate-800">Block new orders</p><p className="text-xs text-slate-400">Credit terms exceeded / on hold</p></div>
              <Switch data-testid="cust-blocked" checked={form.credit_blocked} onCheckedChange={(v) => setForm((s) => ({ ...s, credit_blocked: v }))} disabled={!isAdmin} />
            </div>
            <div><Label>Internal notes</Label><Textarea data-testid="cust-notes" value={form.internal_notes} onChange={(e) => setForm((s) => ({ ...s, internal_notes: e.target.value }))} disabled={!isAdmin} className="mt-1" /></div>
            <Button data-testid="save-customer-btn" onClick={save} disabled={busy || !isAdmin} className="w-full bg-[#0A4F35] hover:bg-[#083D29] text-white font-semibold">Save Profile</Button>
          </div>
        </div>

        {/* Orders + payments */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-lg border border-[#E5E7EB] bg-white">
            <div className="px-5 py-3 border-b border-[#E5E7EB]"><h3 className="font-display font-semibold text-slate-900">Order History</h3></div>
            {c.orders?.length ? (
              <div className="divide-y divide-[#E5E7EB]">
                {c.orders.map((o) => (
                  <Link key={o.id} to={`/admin/orders/${o.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-[#F9FAFB]">
                    <div>
                      <div className="flex items-center gap-2"><span className="font-semibold text-slate-900 text-sm">{o.order_number}</span><OpBadge status={o.operational_status} /></div>
                      <p className="text-xs text-slate-500 mt-0.5">{fmtDate(o.created_at)} · {Number(o.litres).toLocaleString()} L {o.fuel_type}</p>
                    </div>
                    <div className="text-right"><PayBadge status={o.financials.payment_status} /><p className="text-sm font-semibold mt-1">{naira(o.financials.total)}</p></div>
                  </Link>
                ))}
              </div>
            ) : <p className="px-5 py-6 text-sm text-slate-400">No orders yet.</p>}
          </div>

          <div className="rounded-lg border border-[#E5E7EB] bg-white">
            <div className="px-5 py-3 border-b border-[#E5E7EB]"><h3 className="font-display font-semibold text-slate-900">Payment History</h3></div>
            {c.payments?.length ? (
              <div className="divide-y divide-[#E5E7EB]">
                {c.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between px-5 py-3 text-sm">
                    <div><p className="font-medium text-slate-900">{naira(p.amount)} <span className="text-slate-400 font-normal">· {p.method} · {p.order_number}</span></p><p className="text-xs text-slate-400">{fmtDateTime(p.paid_at)} · {p.reference}</p></div>
                  </div>
                ))}
              </div>
            ) : <p className="px-5 py-6 text-sm text-slate-400">No payments yet.</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
